/**
 * Environment-aware logger for SalonHub
 * 
 * This logger provides safe console logging that:
 * - Works normally in development and test environments
 * - Is completely disabled in production to prevent security leaks
 * - Protects sensitive customer and business data
 * - Maintains UI/UX performance with zero overhead in production
 * 
 * Security: All console outputs are eliminated in production builds
 * to prevent exposure of customer data, API keys, and business logic.
 */

// Environment detection
const isDevelopment = import.meta.env.DEV;
const isTest = import.meta.env.MODE === 'test';
const isDebugMode = isDevelopment || isTest;

/**
 * Logger interface with extended methods
 */
interface ExtendedLogger {
  log: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
  info: (...args: unknown[]) => void;
  debug: (...args: unknown[]) => void;
  table: (tabularData?: unknown, properties?: string[]) => void;
  trace: (...args: unknown[]) => void;
  group: (...args: unknown[]) => void;
  groupCollapsed: (...args: unknown[]) => void;
  groupEnd: (...args: unknown[]) => void;
  time: (label?: string) => void;
  timeEnd: (label?: string) => void;
  timeLog: (label?: string, ...args: unknown[]) => void;
  count: (label?: string) => void;
  countReset: (label?: string) => void;
  clear: () => void;
  assert: (condition?: boolean, ...data: unknown[]) => void;
  dir: (item: unknown, options?: unknown) => void;
  dirxml: (...args: unknown[]) => void;
  when: (condition: boolean, ...args: unknown[]) => void;
  logSafe: (...args: unknown[]) => void;
  perf: (label: string) => { end: () => void };
}

/**
 * Safe logger that only outputs in development/test environments
 * All methods return void functions in production for zero overhead
 */
export const logger: ExtendedLogger = {
  /**
   * General logging - use for debugging and development info
   */
  log: isDebugMode ? console.log.bind(console) : () => {},

  /**
   * Error logging - important errors that need tracking
   * Note: In production, use proper error reporting instead
   */
  error: isDebugMode ? console.error.bind(console) : () => {},

  /**
   * Warning logging - for potential issues
   */
  warn: isDebugMode ? console.warn.bind(console) : () => {},

  /**
   * Info logging - for informational messages
   */
  info: isDebugMode ? console.info.bind(console) : () => {},

  /**
   * Debug logging - for detailed debugging (only in development)
   */
  debug: isDevelopment ? console.debug.bind(console) : () => {},

  /**
   * Table logging - for structured data display
   */
  table: isDevelopment ? console.table.bind(console) : () => {},

  /**
   * Trace logging - for stack traces
   */
  trace: isDevelopment ? console.trace.bind(console) : () => {},

  /**
   * Group logging - for grouped console outputs
   */
  group: isDevelopment ? console.group.bind(console) : () => {},
  groupCollapsed: isDevelopment ? console.groupCollapsed.bind(console) : () => {},
  groupEnd: isDevelopment ? console.groupEnd.bind(console) : () => {},

  /**
   * Timing utilities - for performance debugging
   */
  time: isDevelopment ? console.time.bind(console) : () => {},
  timeEnd: isDevelopment ? console.timeEnd.bind(console) : () => {},
  timeLog: isDevelopment ? console.timeLog.bind(console) : () => {},

  /**
   * Counting utility - for debugging loops and iterations
   */
  count: isDevelopment ? console.count.bind(console) : () => {},
  countReset: isDevelopment ? console.countReset.bind(console) : () => {},

  /**
   * Clear console utility
   */
  clear: isDevelopment ? console.clear.bind(console) : () => {},

  /**
   * Assert utility for development testing
   */
  assert: isDevelopment ? console.assert.bind(console) : () => {},

  /**
   * Directory utility for object inspection
   */
  dir: isDevelopment ? console.dir.bind(console) : () => {},
  dirxml: isDevelopment ? console.dirxml.bind(console) : () => {},

  /**
   * Conditional logging helper for complex conditions
   * Usage: logger.when(condition, 'message', data)
   */
  when: (condition: boolean, ...args: unknown[]) => {
    if (condition && isDebugMode) {
      logger.log(...args);
    }
  },

  /**
   * Safe JSON stringify for logging objects without circular references
   * Usage: logger.logSafe('Data:', data)
   */
  logSafe: (...args: unknown[]) => {
    if (!isDebugMode) return;

    const safeArgs = args.map(arg => {
      if (typeof arg === 'object' && arg !== null) {
        try {
          return JSON.parse(JSON.stringify(arg));
        } catch {
          return '[Circular/Complex Object]';
        }
      }
      return arg;
    });

    logger.log(...safeArgs);
  },

  /**
   * Performance logging helper
   * Usage: const timer = logger.perf('operation'); ... timer.end();
   */
  perf: (label: string) => {
    if (!isDevelopment) {
      return { end: () => {} };
    }

    const start = performance.now();
    logger.time(label);

    return {
      end: () => {
        const duration = performance.now() - start;
        logger.timeEnd(label);
        logger.log(`⚡ ${label} took ${duration.toFixed(2)}ms`);
      }
    };
  }
};

// Default export
export default logger;

// Named exports for specific use cases
export const log = logger.log;
export const error = logger.error;
export const warn = logger.warn;
export const info = logger.info;
export const debug = logger.debug;